/**
 * AiAgentLog — 予約エージェント実行ログのレビュー + プロンプト追加ルール管理（Track A, 2026-07-04）
 *
 * タブ1「実行ログ」: agent_execution_logs の一覧・good/bad 評価・メモ。
 *   bad と評価した事例は「ルール化」ボタンでタブ2のルール作成にプリフィルできる。
 * タブ2「追加ルール」: agent_prompt_rules の CRUD。
 *   scope: agent=グローバルエージェント / email=AIメール生成 / line=LINE自動応答。
 *   有効なルールはデプロイ不要で各プロンプトに動的注入される。
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, IconButton, Button, Tabs, Tab,
  Select, MenuItem, TextField, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Switch, FormControlLabel, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useNavigate } from 'react-router-dom';
import { getAgentAction, postAgentAction } from '../agent/api';
import { tierOf } from '../agent/operationRegistry';

const PAGE_SIZE = 30;

interface ExecutionLog {
  id: number;
  session_id: string | null;
  source: string;
  tool_name: string;
  args_json: string | null;
  result_status: 'success' | 'error' | 'cancelled';
  result_summary: string | null;
  reservation_id: string | null;
  duration_ms: number | null;
  evaluation_score: number | null;
  evaluation_note: string | null;
  created_at: string;
}

interface PromptRule {
  id: number;
  scope: string;
  title: string;
  rule_text: string;
  is_active: number;
  sort_order: number;
  updated_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  success: '#10b981',
  error: '#ef4444',
  cancelled: '#94a3b8',
};

const SCOPE_LABELS: Record<string, string> = {
  agent: 'エージェント',
  email: 'AIメール',
  line: 'LINE自動応答',
};

export default function AiAgentLog() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  // 会話記録（2026-08-23）: 「この件は記録して」で保存されたやりとり
  const [records, setRecords] = useState<any[]>([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [openRecord, setOpenRecord] = useState<any | null>(null);
  // 暗黙評価によるツール別の採用率（承認=採用 / 取消=不採用）
  const [toolStats, setToolStats] = useState<any[]>([]);

  // --- 実行ログ ---
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [evaluatedFilter, setEvaluatedFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [noteEditing, setNoteEditing] = useState<Record<number, string>>({});

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAgentAction('get_execution_logs', {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        status: statusFilter,
        evaluated: evaluatedFilter,
      });
      if (res.success) {
        setLogs(res.logs || []);
        setTotal(res.total || 0);
      }
    } catch (e) {
      console.error('[AiAgentLog] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, evaluatedFilter]);

  useEffect(() => { void loadLogs(); }, [loadLogs]);

  const evaluate = useCallback(async (log: ExecutionLog, score: number | null) => {
    const note = noteEditing[log.id] ?? log.evaluation_note ?? '';
    try {
      await postAgentAction('evaluate_execution', { id: log.id, evaluation_score: score, evaluation_note: note });
      setLogs((prev) => prev.map((l) => (l.id === log.id ? { ...l, evaluation_score: score, evaluation_note: note } : l)));
    } catch (e) {
      console.error('[AiAgentLog] evaluate failed', e);
    }
  }, [noteEditing]);

  // --- 追加ルール ---
  const [rules, setRules] = useState<PromptRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [editRule, setEditRule] = useState<Partial<PromptRule> | null>(null);
  const [savingRule, setSavingRule] = useState(false);

  const loadRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      const res = await getAgentAction('get_prompt_rules');
      if (res.success) setRules(res.rules || []);
    } catch (e) {
      console.error('[AiAgentLog] rules load failed', e);
    } finally {
      setRulesLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === 1) void loadRules(); }, [tab, loadRules]);

  const loadRecords = useCallback(async () => {
    try {
      const j: any = await getAgentAction('get_conversations', { limit: '50' });
      setRecords(j?.records || []);
      setRecordsTotal(j?.total || 0);
    } catch (e: any) {
      // 無言の失敗を作らない
      // eslint-disable-next-line no-console
      console.error('[AiAgentLog] 会話記録の取得に失敗:', e?.message || e);
    }
  }, []);

  const loadToolStats = useCallback(async () => {
    try {
      const j: any = await getAgentAction('get_tool_stats', { days: '30' });
      setToolStats(j?.tools || []);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('[AiAgentLog] 採用率の取得に失敗:', e?.message || e);
    }
  }, []);

  useEffect(() => { if (tab === 2) void loadRecords(); }, [tab, loadRecords]);
  useEffect(() => { if (tab === 3) void loadToolStats(); }, [tab, loadToolStats]);

  const saveRule = useCallback(async () => {
    if (!editRule?.title?.trim() || !editRule?.rule_text?.trim()) return;
    setSavingRule(true);
    try {
      await postAgentAction('save_prompt_rule', {
        id: editRule.id,
        scope: editRule.scope || 'agent',
        title: editRule.title,
        rule_text: editRule.rule_text,
        is_active: editRule.is_active ?? 1,
        sort_order: editRule.sort_order ?? 0,
      });
      setEditRule(null);
      await loadRules();
    } catch (e) {
      console.error('[AiAgentLog] rule save failed', e);
    } finally {
      setSavingRule(false);
    }
  }, [editRule, loadRules]);

  const toggleRuleActive = useCallback(async (rule: PromptRule) => {
    await postAgentAction('save_prompt_rule', { ...rule, is_active: rule.is_active ? 0 : 1 });
    await loadRules();
  }, [loadRules]);

  const deleteRule = useCallback(async (rule: PromptRule) => {
    if (!window.confirm(`ルール「${rule.title}」を削除しますか？`)) return;
    await postAgentAction('delete_prompt_rule', { id: rule.id });
    await loadRules();
  }, [loadRules]);

  /** bad 評価の実行ログから反例ルールの下書きを作る */
  const ruleFromLog = useCallback((log: ExecutionLog) => {
    setTab(1);
    setEditRule({
      scope: log.source === 'email' ? 'email' : 'agent',
      title: `反例: ${log.tool_name} の誤用（ログ#${log.id}）`,
      rule_text: `以下のケースでは同じ対応をしないこと。\n状況: ${log.args_json || ''}\n問題: ${(noteEditing[log.id] ?? log.evaluation_note) || log.result_summary || ''}`,
      is_active: 1,
      sort_order: 0,
    });
  }, [noteEditing]);

  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <Box sx={{ p: 2, maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
        <Typography variant="h6" fontWeight={800}>🤖 エージェント実行ログ / 追加ルール</Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`実行ログ (${total})`} />
        <Tab label="追加ルール" />
        <Tab label={`会話記録${recordsTotal ? ` (${recordsTotal})` : ''}`} />
        <Tab label="ツール採用率" />
      </Tabs>

      {tab === 0 && (
        <>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
            <Select size="small" displayEmpty value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
              <MenuItem value="">全ステータス</MenuItem>
              <MenuItem value="success">success</MenuItem>
              <MenuItem value="error">error</MenuItem>
              <MenuItem value="cancelled">cancelled</MenuItem>
            </Select>
            <Select size="small" displayEmpty value={evaluatedFilter} onChange={(e) => { setEvaluatedFilter(e.target.value); setPage(0); }}>
              <MenuItem value="">評価: すべて</MenuItem>
              <MenuItem value="0">未評価のみ</MenuItem>
              <MenuItem value="1">評価済みのみ</MenuItem>
            </Select>
            {loading && <CircularProgress size={18} />}
            <Box sx={{ flex: 1 }} />
            <IconButton size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}><NavigateBeforeIcon /></IconButton>
            <Typography variant="caption">{page + 1} / {maxPage + 1}</Typography>
            <IconButton size="small" disabled={page >= maxPage} onClick={() => setPage((p) => p + 1)}><NavigateNextIcon /></IconButton>
          </Box>

          {logs.map((log) => (
            <Paper key={log.id} sx={{ p: 1.5, mb: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip size="small" label={log.result_status} sx={{ bgcolor: STATUS_COLORS[log.result_status], color: '#fff', fontWeight: 700 }} />
                <Chip size="small" variant="outlined" label={log.source} />
                <Typography variant="body2" fontWeight={700}>{log.tool_name}</Typography>
                {log.reservation_id && <Chip size="small" label={`予約 ${log.reservation_id}`} variant="outlined" color="info" />}
                {log.duration_ms != null && <Typography variant="caption" color="text.secondary">{log.duration_ms}ms</Typography>}
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">{log.created_at}</Typography>
              </Box>
              {log.args_json && (
                <Typography variant="caption" sx={{ display: 'block', color: '#64748b', wordBreak: 'break-all', mt: 0.5 }}>
                  引数: {log.args_json.slice(0, 300)}
                </Typography>
              )}
              {log.result_summary && (
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {log.result_summary.slice(0, 400)}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <Tooltip title="良い実行">
                  <IconButton size="small" color={log.evaluation_score === 1 ? 'success' : 'default'} onClick={() => evaluate(log, log.evaluation_score === 1 ? null : 1)}>
                    <ThumbUpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="問題のある実行">
                  <IconButton size="small" color={log.evaluation_score === 0 ? 'error' : 'default'} onClick={() => evaluate(log, log.evaluation_score === 0 ? null : 0)}>
                    <ThumbDownIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <TextField
                  size="small"
                  placeholder="評価メモ（何が問題だったか）"
                  value={noteEditing[log.id] ?? log.evaluation_note ?? ''}
                  onChange={(e) => setNoteEditing((prev) => ({ ...prev, [log.id]: e.target.value }))}
                  onBlur={() => {
                    if (log.evaluation_score !== null) void evaluate(log, log.evaluation_score);
                  }}
                  sx={{ flex: 1 }}
                />
                {log.evaluation_score === 0 && (
                  <Button size="small" variant="outlined" onClick={() => ruleFromLog(log)}>
                    ルール化 →
                  </Button>
                )}
              </Box>
            </Paper>
          ))}
          {logs.length === 0 && !loading && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              実行ログはまだありません（エージェント/AIメールの tool 実行が記録されます）
            </Typography>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          <Box sx={{ display: 'flex', mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              有効なルールは各プロンプトに自動注入されます（デプロイ不要）。
            </Typography>
            <Button variant="contained" size="small" onClick={() => setEditRule({ scope: 'agent', title: '', rule_text: '', is_active: 1, sort_order: 0 })}>
              ＋ ルール追加
            </Button>
          </Box>
          {rulesLoading && <CircularProgress size={18} />}
          {rules.map((rule) => (
            <Paper key={rule.id} sx={{ p: 1.5, mb: 1, opacity: rule.is_active ? 1 : 0.55 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip size="small" label={SCOPE_LABELS[rule.scope] || rule.scope} color="secondary" variant="outlined" />
                <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>{rule.title}</Typography>
                <FormControlLabel
                  control={<Switch size="small" checked={!!rule.is_active} onChange={() => toggleRuleActive(rule)} />}
                  label={<Typography variant="caption">{rule.is_active ? '有効' : '無効'}</Typography>}
                />
                <Button size="small" onClick={() => setEditRule(rule)}>編集</Button>
                <Button size="small" color="error" onClick={() => deleteRule(rule)}>削除</Button>
              </Box>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', color: '#475569' }}>
                {rule.rule_text}
              </Typography>
            </Paper>
          ))}
          {rules.length === 0 && !rulesLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              追加ルールはまだありません
            </Typography>
          )}
        </>
      )}

      {/* ルール編集ダイアログ */}
      <Dialog open={!!editRule} onClose={() => setEditRule(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editRule?.id ? 'ルールを編集' : 'ルールを追加'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <Select
            size="small"
            value={editRule?.scope || 'agent'}
            onChange={(e) => setEditRule((prev) => ({ ...prev, scope: e.target.value }))}
          >
            <MenuItem value="agent">エージェント（予約操作）</MenuItem>
            <MenuItem value="email">AIメール生成</MenuItem>
            <MenuItem value="line">LINE自動応答</MenuItem>
          </Select>
          <TextField
            label="タイトル"
            size="small"
            value={editRule?.title || ''}
            onChange={(e) => setEditRule((prev) => ({ ...prev, title: e.target.value }))}
          />
          <TextField
            label="ルール本文（プロンプトにこのまま注入されます）"
            multiline
            rows={6}
            value={editRule?.rule_text || ''}
            onChange={(e) => setEditRule((prev) => ({ ...prev, rule_text: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRule(null)}>キャンセル</Button>
          <Button
            variant="contained"
            disabled={savingRule || !editRule?.title?.trim() || !editRule?.rule_text?.trim()}
            onClick={saveRule}
          >
            {savingRule ? '保存中…' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 会話記録（2026-08-23）: 「この件は記録して」で保存されたやりとり */}
      {tab === 2 && (
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            エージェントに「この件は記録して」と伝えると、そこまでのやりとりが丸ごと保存されます。
            うまくいかなかった指示を見返して、ツールやルールの改善に使ってください。
          </Typography>
          {records.length === 0 && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              まだ記録はありません
            </Paper>
          )}
          {records.map((r) => (
            <Paper key={r.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`#${r.id}`} />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {r.note || '(理由の記載なし)'}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {r.page && <Chip size="small" variant="outlined" label={r.page} />}
                {Number(r.reviewed) === 1 && <Chip size="small" color="success" label="確認済み" />}
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {r.operator || '—'} / {r.created_at}
                </Typography>
                <Button size="small" onClick={() => setOpenRecord(r)}>全文を見る</Button>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* ツール採用率（暗黙評価）: 承認=採用 / 取消=不採用。tier B→A 昇格の判定材料 */}
      {tab === 3 && (
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            直近30日。<strong>確認カードの承認を「採用」、取消を「不採用」</strong>として自動集計しています
            （good/bad を押す必要はありません）。昇格の目安は 50件以上・採用率80%以上・bad評価ゼロです。
          </Typography>
          <Paper variant="outlined">
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr repeat(5, 90px)', gap: 0, p: 1, fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box>ツール</Box><Box textAlign="right">実行</Box><Box textAlign="right">承認</Box>
              <Box textAlign="right">取消</Box><Box textAlign="right">失敗</Box><Box textAlign="right">採用率</Box>
            </Box>
            {toolStats.map((t) => (
              <Box key={t.tool_name}
                sx={{ display: 'grid', gridTemplateColumns: '1fr 56px repeat(5, 90px)', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                  {t.tool_name}
                  {/* 昇格の余地があるのは tier B のみ。tier A は既に自動実行なので出さない */}
                  {t.promotable && tierOf(t.tool_name) === 'B' && (
                    <Chip size="small" color="success" label="昇格可" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                  )}
                </Box>
                <Box textAlign="center">
                  <Chip size="small" variant="outlined" label={tierOf(t.tool_name) || '—'} sx={{ height: 18, fontSize: '0.6rem' }} />
                </Box>
                <Box textAlign="right">{t.total}</Box>
                <Box textAlign="right">{t.success}</Box>
                <Box textAlign="right">{t.cancelled}</Box>
                <Box textAlign="right" sx={{ color: Number(t.error) > 0 ? 'error.main' : undefined }}>{t.error}</Box>
                <Box textAlign="right" sx={{ fontWeight: 700 }}>
                  {t.adoption_rate == null ? '—' : `${t.adoption_rate}%`}
                </Box>
              </Box>
            ))}
            {toolStats.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>データがありません</Box>
            )}
          </Paper>
        </Box>
      )}

      {/* 会話記録の全文 */}
      <Dialog open={!!openRecord} onClose={() => setOpenRecord(null)} maxWidth="md" fullWidth>
        <DialogTitle>会話記録 #{openRecord?.id} — {openRecord?.note || '(理由の記載なし)'}</DialogTitle>
        <DialogContent dividers>
          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem', fontFamily: 'inherit' }}>
            {openRecord?.transcript}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              try {
                await postAgentAction('review_conversation', { id: openRecord.id, reviewed: 1 });
                setOpenRecord(null);
                void loadRecords();
              } catch (e: any) {
                // eslint-disable-next-line no-console
                console.error('[AiAgentLog] 確認済みにできませんでした:', e?.message || e);
              }
            }}
          >
            確認済みにする
          </Button>
          <Button onClick={() => setOpenRecord(null)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
